import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { useAuth } from './contexts/AuthContext';
import './Home.css';

function Home() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // State to manage video playback
  const [playVideo, setPlayVideo] = useState(false);

  // NEW: State to manage image loading error safely
  const [imageError, setImageError] = useState(false);

  /* ---------------- Navigation ---------------- */

  const handleLoginClick = () => navigate('/login');
  const handleSignupClick = () => navigate('/signup');

  const handleGetStartedClick = () => {
    navigate(currentUser ? '/dashboard' : '/login');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  /* ---------------- Render ---------------- */

  return (
    <div className="home-container">
      <Header
        user={currentUser}
        onLogout={handleLogout}
        onLogin={handleLoginClick}
        onSignup={handleSignupClick}
      />

      <main className="home-main">
        {/* ================= HERO ================= */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">
              Bridging PDFs and AI
            </h1>
            <p className="hero-subtitle">
              90% of the world's formal knowledge ,from academic papers to technical manuals, are locked in PDFs. Upload your files, configure how you want to be taught, and learn directly from the source—distraction-free.
            </p>

            <button onClick={handleGetStartedClick} className="hero-cta-button">
              {currentUser ? 'Go to Dashboard' : 'Get Started Free'}
            </button>

            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">1,000+</span>
                <span className="stat-label">PDFs Analyzed</span>
              </div>
              <div className="stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">RAG Accuracy Rate</span>
              </div>
              <div className="stat">
                <span className="stat-number">24/7</span>
                <span className="stat-label">FREE AI SUPPRT</span>
              </div>
            </div>
          </div>

          <div className="hero-image">
            {/* FULL-IMAGE MOCKUP */}
            <div className="mockup-full-image">
              <div className="mockup-image-container">
                {/* FIXED LOGIC: 
                   If imageError is false, try to show IMG.
                   If imageError is true (onError fired), show the Fallback div.
                */}
                {!imageError ? (
                  <img
                    src="/dashboard.png" /* Added slash for absolute path */
                    alt="PDF analysis dashboard preview"
                    className="mockup-img"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="mockup-overlay">
                    <div className="overlay-icon">📊</div>
                    <h3>Dashboard Preview</h3>
                    <p>Interactive PDF analysis interface</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ================= VIDEO (GUARANTEED) ================= */}
        <section className="video-section">
          <div className="section-header">
            <h2 className="section-title">See PDFraider in Action</h2>
          </div>

          <div className="video-container">
            <div className="video-wrapper">
              <iframe
                className="youtube-iframe"
                src="https://www.youtube.com/embed/videoseries?list=PLK0nr5rx5uIyI-_1hVudH8HDQU2l9z8Ao"
                title="PDFraider Demo"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* ================= FEATURES ================= */}
        <section id="features" className="features-section">
          <div className="section-header">
            <h2 className="section-title">Why Choose PDFraider?</h2>
            <p className="section-subtitle">
              Timely features designed to enhance your document workflow
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3>Smart Search</h3>
              <p>AI-powered semantic search across all your documents.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>AI Chat Assistant</h3>
              <p>Ask questions and get personality-aware explanations.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>In-Document Notes</h3>
              <p>Take and export notes directly while on your PDF viewer</p>
            </div>


            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Enhanced Focus</h3>
              <p>Document-grounded answers and reduced conversation drift.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">👓</div>
              <h3>Comfortable Reading</h3>
              <p>Reduce eye strain and an all-in-one learning experience.</p>
            </div>


            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Multi-language</h3>
              <p>Learn in 50+ languages with native understanding.</p>
            </div>
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section id="how-it-works" className="how-it-works-section">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Five simple steps to turn your documents into a personal tutor
            </p>
          </div>

          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Connect Your Gemini API Key</h3>
                <p>Grab your free Google Gemini API key to get started. It takes seconds to retrieve.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Upload Your Knowledge Base</h3>
                <p>Upload your document, course material, or manual into PDFraider.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Instant Analysis</h3>
                <p>Our engine reads, indexes, and organizes your document's information for retrieval.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Ask Complex Questions</h3>
                <p>Chat naturally with your files. PDFraider cites the exact page where it found the answer.</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Synthesize & Export</h3>
                <p>Take side-by-side notes as you learn, then export your summary with a single click.</p>
              </div>
            </div>
          </div>
        </section>



        {/* ================= CTA ================= */}
        <section className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Learning Experience?</h2>
            <p className="cta-subtitle">

            </p>

            <div className="cta-buttons">
              <button onClick={handleGetStartedClick} className="cta-primary">
                {currentUser ? 'Go to Dashboard' : 'Start Free Trial'}
              </button>

              {!currentUser && (
                <button onClick={handleLoginClick} className="cta-secondary">
                  Already have an account? Login
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Home;